/**
 * Where a completed sign-in is allowed to land (Phase 3B Task 7, fix
 * round 1 / Ruling O11).
 *
 * This MIRRORS the server predicate in `apps/api/src/routes/auth.ts`
 * (`RETURN_TO_RE` / `safeReturnTo`) character for character. Two copies
 * because the two halves cannot import each other, and both are needed:
 * the API validates what it puts INTO the cookie and what comes back out,
 * and this page must not trust a query parameter either — `/auth/callback`
 * is a public page and its landing comes straight off the URL.
 *
 * WHY A CHARACTER ALLOW-LIST AND NOT A PREFIX CHECK. The first version of
 * this check was `/^\/(?!\/|\\)/`, and it is bypassable: the WHATWG URL
 * parser strips every ASCII tab, LF and CR from its input BEFORE parsing,
 * so `/<TAB>/evil.test` — whose second character is not `/` — collapses
 * into the protocol-relative `//evil.test` and resolves to
 * `https://evil.test/`. `useSearchParams().get()` percent-decodes, so
 * `?returnTo=%2F%09%2Fevil.test` is all an attacker needs to turn the
 * firm's own login into a phishing landing page. An allow-list of the
 * characters a path may contain refuses that by construction rather than
 * by enumerating parser quirks, and the decode-once re-check below refuses
 * the percent-encoded spelling of the same trick.
 */
const RETURN_TO_RE = /^\/(?!\/|\\)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?[\]]{0,512}$/;

export const DEFAULT_LANDING = '/cases';

/**
 * The value if it is a same-origin path, else `null`.
 *
 * Rejects: anything not a string, the empty string, absolute URLs and
 * scheme-ish values (`https://…`, `javascript:…` — neither starts with
 * `/`), protocol-relative `//host` and `/\host`, every control and
 * whitespace character, any character outside the RFC 3986 path/query set,
 * anything over 512 characters, and — after ONE decode — any of the above
 * smuggled in percent-encoded (`/%09/evil.test`).
 */
export function safeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (!RETURN_TO_RE.test(value)) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // A malformed escape sequence is not a path we are willing to guess at.
    return null;
  }
  if (decoded !== value && !RETURN_TO_RE.test(decoded)) return null;

  return value;
}

/**
 * The landing URL to navigate to: a validated `returnTo`, or the default.
 *
 * The final belt-and-braces step is resolving the candidate the way the
 * browser will (`new URL(candidate, origin)`) and refusing anything whose
 * origin is not this one — so even a predicate miss cannot become an
 * off-origin navigation.
 */
export function safeLanding(value: unknown, fallback: string = DEFAULT_LANDING): string {
  const candidate = safeReturnTo(value);
  if (!candidate) return fallback;

  if (typeof window !== 'undefined') {
    try {
      if (new URL(candidate, window.location.origin).origin !== window.location.origin) {
        return fallback;
      }
    } catch {
      return fallback;
    }
  }

  return candidate;
}
